// src/Pages/finanzas-clientes/useArriendosSection.ts
import { useCallback, useEffect, useState } from "react";
import { getMsg } from "../../utils/antdStatic";
import dayjs, { Dayjs } from "dayjs";
import { apiFetch } from "../../services/apiClient";
import { normalizeJsonb, JsonbPair, JsonbItem } from "./sections";
import { ahoraChile } from "../../utils/fechaChile";

export type BookingRaw = {
  id: number;
  fecha_creacion: string | null;
  id_propiedad: number | null;
  nombrepropiedad: string | null;
  id_propiedadl: string | null;
  id_reserval: string | null;
  id_reservac: string | null;
  id_reserva_plataforma: string | null;
  nombre_huesped: string | null;
  telefono_huesped: string | null;
  numero_huespedes: number | null;
  ingreso: string | null;
  salida: string | null;
  noches: number | null;
  id_plataforma: string | null;
  nombre_plataforma: string | null;
  moneda: string | null;
  cambio_usd_clp: number | string | null;
  reserva_total_clp: number | string | null;
  reserva_total_usd: number | string | null;
  precio_noches_clp: number | string | null;
  precio_noches_usd: number | string | null;
  limpieza_clp: number | string | null;
  limpieza_usd: number | string | null;
  comision_plataforma_clp: number | string | null;
  comision_plataforma_usd: number | string | null;
  extras_comision_clp: JsonbItem[] | null;
  extras_comision_usd: JsonbItem[] | null;
  extras_sin_comision_clp: JsonbItem[] | null;
  extras_sin_comision_usd: JsonbItem[] | null;
  impuesto_plataforma_clp: JsonbItem[] | null;
  impuesto_plataforma_usd: JsonbItem[] | null;
  descuentos_clp: JsonbItem[] | null;
  descuentos_usd: JsonbItem[] | null;
  tasa_comision_af: number | string | null; // %
  comision_af_noches_clp: number | string | null;
  comision_af_extras_clp: number | string | null;
  comision_af_noches_usd: number | string | null;
  comision_af_extras_usd: number | string | null;
  iva_comision_de_noches_clp: number | string | null;
  iva_comision_de_extras_clp: number | string | null;
  iva_comision_de_noches_usd: number | string | null;
  iva_comision_de_extras_usd: number | string | null;
  total_extras_clp: number | string | null;
  total_extras_usd: number | string | null;
  reserva_total_propietario: number | string | null;
  estado_pago: string | null;
  tipo_pago: string | null;
  action: string | null;
  tipo: string | null;
  inmutable: boolean | null;
  /**
   * visible_cliente: controla si el Cliente ve este arriendo con los
   * montos. Si false, se debe mostrar como "En revisión" sin montos.
   * Ver docs/flujo-financiero.md §13. Puede venir como `visible_cliente`
   * (snake case raw) o `visibleCliente` (camelCase mapeado).
   */
  visible_cliente?: boolean | null;
  visibleCliente?: boolean | null;
  notainterna: number | string | null; // en el modelo es NUMERIC
};

export type ArriendoRow = {
  id: number;
  fechaCreacion: string | null;
  idPropiedad: number | null;
  nombrePropiedad: string | null;
  idPropiedadL: string | null;
  idReservaV: string | null;
  idReservaC: string | null;
  idReservaPlataforma: string | null;
  nombreHuesped: string | null;
  telefonoHuesped: string | null;
  huespedes: number | null;
  ingreso: string | null;
  salida: string | null;
  noches: number | null;
  idPlataforma: string | null;
  plataforma: string | null;
  moneda: string | null;
  cambioUsdClp: number | null;
  reservaTotalClp: number | null;
  reservaTotalUsd: number | null;
  precioNochesClp: number | null;
  precioNochesUsd: number | null;
  limpiezaClp: number | null;
  limpiezaUsd: number | null;
  comisionPlataformaClp: number | null;
  comisionPlataformaUsd: number | null;
  extrasComisionClp: JsonbPair[] | null;
  extrasComisionUsd: JsonbPair[] | null;
  extrasSinComisionClp: JsonbPair[] | null;
  extrasSinComisionUsd: JsonbPair[] | null;
  impuestoPlataformaClp: JsonbPair[] | null;
  impuestoPlataformaUsd: JsonbPair[] | null;
  descuentosClp: JsonbPair[] | null;
  descuentosUsd: JsonbPair[] | null;
  tasaComisionAf: number | null;
  comisionAfNochesClp: number | null;
  comisionAfExtrasClp: number | null;
  comisionAfNochesUsd: number | null;
  comisionAfExtrasUsd: number | null;
  ivaComisionDeNochesClp: number | null;
  ivaComisionDeExtrasClp: number | null;
  ivaComisionDeNochesUsd: number | null;
  ivaComisionDeExtrasUsd: number | null;
  totalExtrasClp: number | null;
  totalExtrasUsd: number | null;
  totalPropietario: number | null;
  estadoPago: string | null;
  tipoPago: string | null;
  action: string | null;
  tipo: string | null;
  inmutable: boolean | null;
  /**
   * Si `false`, este arriendo está "En revisión" y el Cliente NO debe
   * ver los montos — sólo fechas y huésped. Se publica automáticamente
   * el 2do día hábil del mes, o manual por admin/superadmin. Ver
   * docs/flujo-financiero.md §13.
   */
  visibleCliente: boolean;
  notaInterna: number | string | null;
};

const toNumber = (n: any): number => {
  const x = typeof n === "string" ? Number(n) : n;
  return Number.isFinite(x) ? Number(x) : 0;
};

export function useArriendosSection(selectedPropId: number | null) {
  const [rows, setRows] = useState<ArriendoRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState<Dayjs | null>(null);
  const [q, setQ] = useState<string | undefined>(undefined);
  const [totals, setTotals] = useState<{
    totalPropietario: number;
  } | null>(null);

  function mapRow(b: BookingRaw): ArriendoRow {
    return {
      id: b.id,
      fechaCreacion: b.fecha_creacion,
      idPropiedad: b.id_propiedad,
      nombrePropiedad: b.nombrepropiedad,
      idPropiedadL: b.id_propiedadl,
      idReservaV: b.id_reserval,
      idReservaC: b.id_reservac,
      idReservaPlataforma: b.id_reserva_plataforma,
      nombreHuesped: b.nombre_huesped || "",
      telefonoHuesped: b.telefono_huesped,
      huespedes: toNumber(b.numero_huespedes),
      ingreso: b.ingreso,
      salida: b.salida,
      noches: toNumber(b.noches),
      idPlataforma: b.id_plataforma,
      plataforma: b.nombre_plataforma || "",
      moneda: b.moneda,
      cambioUsdClp: toNumber(b.cambio_usd_clp),
      reservaTotalClp: toNumber(b.reserva_total_clp),
      reservaTotalUsd: toNumber(b.reserva_total_usd),
      precioNochesClp: toNumber(b.precio_noches_clp),
      precioNochesUsd: toNumber(b.precio_noches_usd),
      limpiezaClp: toNumber(b.limpieza_clp),
      limpiezaUsd: toNumber(b.limpieza_usd),
      comisionPlataformaClp: toNumber(b.comision_plataforma_clp),
      comisionPlataformaUsd: toNumber(b.comision_plataforma_usd),
      extrasComisionClp: normalizeJsonb(b.extras_comision_clp),
      extrasComisionUsd: normalizeJsonb(b.extras_comision_usd),
      extrasSinComisionClp: normalizeJsonb(b.extras_sin_comision_clp),
      extrasSinComisionUsd: normalizeJsonb(b.extras_sin_comision_usd),
      descuentosClp: normalizeJsonb(b.descuentos_clp),
      descuentosUsd: normalizeJsonb(b.descuentos_usd),
      impuestoPlataformaClp: normalizeJsonb(b.impuesto_plataforma_clp),
      impuestoPlataformaUsd: normalizeJsonb(b.impuesto_plataforma_usd),
      tasaComisionAf: toNumber(b.tasa_comision_af),
      comisionAfNochesClp: toNumber(b.comision_af_noches_clp),
      comisionAfExtrasClp: toNumber(b.comision_af_extras_clp),
      comisionAfNochesUsd: toNumber(b.comision_af_noches_usd),
      comisionAfExtrasUsd: toNumber(b.comision_af_extras_usd),
      ivaComisionDeNochesClp: toNumber(b.iva_comision_de_noches_clp),
      ivaComisionDeExtrasClp: toNumber(b.iva_comision_de_extras_clp),
      ivaComisionDeNochesUsd: toNumber(b.iva_comision_de_noches_usd),
      ivaComisionDeExtrasUsd: toNumber(b.iva_comision_de_extras_usd),
      totalExtrasClp: toNumber(b.total_extras_clp),
      totalExtrasUsd: toNumber(b.total_extras_usd),
      totalPropietario: toNumber(b.reserva_total_propietario),
      estadoPago: b.estado_pago,
      tipoPago: b.tipo_pago,
      action: b.action,
      tipo: b.tipo,
      inmutable: b.inmutable,
      // El backend devuelve camelCase `visibleCliente` vía bookingToFrontend.
      // Por compat también aceptamos `visible_cliente` raw. Default FALSE:
      // si falta el dato, asumimos no-publicado para no exponer montos.
      visibleCliente: Boolean(
        (b as any).visibleCliente ?? (b as any).visible_cliente ?? false,
      ),
      notaInterna: toNumber(b.notainterna),
    };
  }

  const fetchRows = useCallback(async () => {
    if (selectedPropId == null) return; // evita cortar si fuera 0 por accidente

    try {
      setLoading(true);

      // monthStr seguro en formato YYYY-MM
      const monthStr = month
        ? dayjs(month).format("YYYY-MM")
        : ahoraChile().format("YYYY-MM");

      const params = new URLSearchParams();
      params.set("propId", String(selectedPropId));
      params.set("month", monthStr);

      const json = await apiFetch<any>(
        `/finanzas/arriendos?${params.toString()}`,
      );
      if (!json) throw new Error("No se pudo obtener arriendos");

      // Soporta ambos formatos: array puro o {rows: [...]}
      const rawArray = Array.isArray(json)
        ? json
        : json.rows || json.data || [];

      const mapped: ArriendoRow[] = rawArray.map(mapRow);
      setRows(mapped);
      // Total por arriendos (único total que mostramos)
      const totalPropietario = mapped.reduce(
        (acc, r) => acc + (r.totalPropietario ?? 0),
        0,
      );
      setTotals({ totalPropietario });

      // Si el backend no pagina, usa largo local
      setTotal(
        Array.isArray(json.total) ? json.total : (json.total ?? mapped.length),
      );
      setPage(json.page ?? 1);
      setSize(json.size ?? 20);
    } catch (e: any) {
      getMsg()?.error(e?.message || "Error cargando arriendos");
      setRows([]); // opcional: limpia en error
      setTotals({ totalPropietario: 0 });
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [selectedPropId, month]);

  useEffect(() => {
    if (selectedPropId && month) fetchRows();
  }, [selectedPropId, month]);

  return {
    rows,
    total,
    page,
    size,
    loading,
    totals,
    month,
    setMonth,
    q,
    setQ,
    onPaginate: () => fetchRows(),
    refetch: fetchRows,
  };
}
